import { z } from 'zod';
import { LICENSE_STATUS_LIST } from '../constants/license.js';
import {
  address,
  email,
  fssai,
  gstin,
  isoDate,
  name,
  objectId,
  optional,
  outletCode,
  pagination,
  pan,
  phone,
  requiredText,
  slug,
  text,
  timezone,
} from './common.js';
import { featuresSchema, graceDays, limitsSchema } from './license.js';
import { outletFields } from './outlet.js';

export const clientProfileFields = {
  businessName: requiredText(120),
  legalName: optional(text(160)),
  gstin: optional(gstin),
  fssaiNo: optional(fssai),
  pan: optional(pan),
  address,
  timezone: timezone.default('Asia/Kolkata'),
  language: z.enum(['en', 'hi', 'te']).default('en'),
  showPoweredBy: z.boolean().default(true),
  replyToEmail: optional(email),
};

export const createClientSchema = z
  .strictObject({
    ...clientProfileFields,
    code: optional(slug),
    owner: z.strictObject({ name, email, phone }),
    license: z.strictObject({
      planId: objectId,
      startDate: isoDate,
      expiryDate: isoDate,
      graceDays,
      limits: limitsSchema,
      features: featuresSchema,
    }),
    credentialsMode: z.enum(['link', 'temp_password']).default('link'),
    firstOutlet: z
      .strictObject({
        name: outletFields.name,
        code: outletCode,
        address: address.optional(),
        phone: outletFields.phone,
      })
      .optional(),
  })
  .refine((v) => v.license.expiryDate >= v.license.startDate, {
    message: 'Expiry must be on or after the start date',
    path: ['license', 'expiryDate'],
  });

export const updateClientSchema = z.strictObject({
  businessName: clientProfileFields.businessName.optional(),
  legalName: clientProfileFields.legalName,
  gstin: clientProfileFields.gstin,
  fssaiNo: clientProfileFields.fssaiNo,
  pan: clientProfileFields.pan,
  address: address.optional(),
  timezone: timezone.optional(),
  language: z.enum(['en', 'hi', 'te']).optional(),
  showPoweredBy: z.boolean().optional(),
  replyToEmail: clientProfileFields.replyToEmail,
  owner: z.strictObject({ name: name.optional(), email: email.optional(), phone: phone.optional() }).optional(),
});

export const clientListQuery = z.strictObject({
  q: optional(text(100)),
  status: z.enum(LICENSE_STATUS_LIST).optional(),
  expiringWithin: z.coerce
    .number()
    .int()
    .refine((v) => [7, 15, 30].includes(v))
    .optional(),
  plan: optional(text(40)),
  sort: z.enum(['newest', 'oldest', 'expiry', 'name']).default('newest'),
  ...pagination,
});

/** Admin "view as client" (read-only, 30 minutes). */
export const impersonateSchema = z.strictObject({
  reason: requiredText(200),
  outletId: optional(objectId),
});

export const impersonationTicketSchema = z.strictObject({ token: z.string().trim().min(20).max(200) });

export const noteSchema = z.strictObject({
  body: z.string().trim().min(1, 'Required').max(5000),
  pinned: z.boolean().default(false),
});
